/** ClasificacionCascadeSelect — 5 comboboxes en cascada con búsqueda/autocompletado.
 *
 * Cada nivel se filtra por el parent_id seleccionado en el nivel anterior.
 * Cada combobox tiene un input de búsqueda (cmdk) para tenants con muchos nodos.
 * El usuario puede confirmar la selección en cualquier nivel intermedio.
 *
 * Props:
 *  - tree:        list<{id, nombre, parent_id, nivel, activo}> del tenant.
 *  - value:       UUID actualmente seleccionado (puede ser null).
 *  - disabled:    bool — todos los selectores readonly.
 *  - onChange(newId): callback al confirmar selección.
 *  - onCancel():  callback al cancelar (opcional).
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../components/ui/command';
import { Check, ChevronsUpDown, X, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';

const NIVELES = [1, 2, 3, 4, 5];
const SEP = ' › ';

function buildPathIds(tree, leafId) {
  if (!leafId) return [];
  const byId = new Map(tree.map((n) => [n.id, n]));
  const path = [];
  let cur = byId.get(leafId);
  const seen = new Set();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur.id);
    cur = cur.parent_id ? byId.get(cur.parent_id) : null;
  }
  return path;
}

function LevelCombobox({ level, options, value, onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  const placeholder = options.length ? 'Buscar o seleccionar…' : '—';
  const buttonLabel = selected ? selected.nombre : (options.length ? 'Seleccionar…' : 'Sin opciones');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || options.length === 0}
          className={cn(
            'h-8 text-xs justify-between font-normal flex-1',
            !selected && 'text-slate-400',
          )}
          data-testid={`clasificacion-nivel-${level}`}
        >
          <span className="truncate">{buttonLabel}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[280px]" align="start">
        <Command>
          <CommandInput placeholder={placeholder} className="h-9" data-testid={`clasificacion-buscar-${level}`} />
          <CommandList>
            <CommandEmpty>Sin coincidencias.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => { onPick(null); setOpen(false); }}
                className="text-slate-400 italic"
                data-testid={`clasificacion-nivel-${level}-clear`}
              >
                — Sin selección —
              </CommandItem>
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={`${o.nombre} ${o.id}`}
                  onSelect={() => { onPick(o.id); setOpen(false); }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === o.id ? 'opacity-100' : 'opacity-0')} />
                  {o.nombre}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ClasificacionCascadeSelect({
  tree,
  value,
  disabled = false,
  onChange,
  onCancel,
  hideActions = false,   // modo embebido: sin botones Confirmar/Cancelar
  onLiveChange,          // callback en vivo con el id seleccionado (modo embebido)
}) {
  const initialPath = useMemo(() => {
    const ids = buildPathIds(tree, value);
    const out = [null, null, null, null, null];
    ids.forEach((id, idx) => { if (idx < 5) out[idx] = id; });
    return out;
  }, [tree, value]);

  const [path, setPath] = useState(initialPath);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPath(initialPath); }, [initialPath]);

  const optsByLevel = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 5; i++) {
      const parentId = i === 0 ? null : path[i - 1];
      if (i > 0 && !parentId) { arr.push([]); continue; }
      const opts = tree
        .filter((n) => n.activo !== false && n.nivel === (i + 1) && (n.parent_id || null) === parentId)
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0) || a.nombre.localeCompare(b.nombre));
      arr.push(opts);
    }
    return arr;
  }, [tree, path]);

  const handlePick = (levelIdx, newId) => {
    const next = [...path];
    next[levelIdx] = newId;
    for (let j = levelIdx + 1; j < 5; j++) next[j] = null;
    setPath(next);
  };

  const finalId = useMemo(() => {
    for (let i = 4; i >= 0; i--) if (path[i]) return path[i];
    return null;
  }, [path]);

  useEffect(() => {
    if (hideActions && onLiveChange) onLiveChange(finalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalId, hideActions]);

  const preview = useMemo(() => {
    const names = [];
    const byId = new Map(tree.map((n) => [n.id, n]));
    for (const id of path) {
      if (!id) break;
      const n = byId.get(id);
      if (n) names.push(n.nombre);
    }
    return names.join(SEP);
  }, [path, tree]);

  const changed = finalId !== (value || null);

  const handleConfirmar = async () => {
    setSaving(true);
    try {
      await onChange(finalId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="clasificacion-cascade">
      {NIVELES.map((lvl, idx) => {
        const opts = optsByLevel[idx] || [];
        if (idx > 0 && opts.length === 0 && !path[idx]) return null;
        return (
          <div key={lvl} className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 w-8 shrink-0">N{lvl}</span>
            <LevelCombobox
              level={lvl}
              options={opts}
              value={path[idx]}
              onPick={(id) => handlePick(idx, id)}
              disabled={disabled}
            />
          </div>
        );
      })}

      {preview && (
        <div className="text-[11px] text-slate-500 italic break-words pl-10" data-testid="clasificacion-preview">
          {preview}
        </div>
      )}

      {!disabled && !hideActions && (
        <div className="flex justify-end gap-2 pt-1">
          {onCancel && (
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving} data-testid="btn-cancelar-clasificacion">
              <X className="w-3.5 h-3.5 mr-1" /> Cancelar
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleConfirmar}
            disabled={!changed || saving}
            data-testid="btn-confirmar-clasificacion"
          >
            {saving
              ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Guardando…</>
              : <><Check className="w-3.5 h-3.5 mr-1" /> Confirmar</>}
          </Button>
        </div>
      )}
    </div>
  );
}
